import { QueryClient } from '@tanstack/react-query';

import { PaymentTypes } from 'modules/Tax/TaxPayment/constants';
import businessTaxCalcKeys from 'services/tax/queries/useBusinessTaxCalc/queryKeys';
import calculateTaxKeys from 'services/tax/queries/useCalculateTax/queryKeys';
import safeHarborCalcKeys from 'services/tax/queries/useSafeHarborCalc/queryKeys';

/**
 * The payment types the API accepts.
 *
 * Derived here rather than from `PaymentTypes`, which is a plain object with no `as const` — its
 * values widen to `string`, so it cannot supply literals. Marking that shared constant `as const`
 * would be the better fix, but it has 136 usages across 34 files and belongs in its own change.
 */
export type PaymentType =
    | 'corporate'
    | 'franchise'
    | 'business'
    | 'passthrough_entity'
    | 'annual'
    | 'quarterly'
    | 'extension'
    | 'other';

/**
 * Drops every cached estimate calculation, whatever produced it.
 *
 * The three calculators are separate endpoints under separate key namespaces, so anything that
 * changes an input all of them read — a tax profile edit, say — has to name all three. All cache
 * with `staleTime: Infinity`, and `calculateTaxKeys.byParams` carries no `state` at all, so for
 * that one invalidation is the only thing that can ever refresh it.
 */
export function invalidateTaxCalculators(queryClient: QueryClient) {
    return Promise.all([
        queryClient.invalidateQueries({ queryKey: calculateTaxKeys.all }),
        queryClient.invalidateQueries({ queryKey: safeHarborCalcKeys.all }),
        queryClient.invalidateQueries({ queryKey: businessTaxCalcKeys.all }),
    ]);
}

/**
 * Invalidates the calculators whose figures a payment of this type feeds.
 *
 * Quarterly and business payments read disjoint rows, so a payment only ever moves one side:
 * self-calculate and safe harbor both net `payment_type=QUARTERLY` (`get_quarterly_taxes_paid`,
 * `previous_*_payments`); business-tax-calc nets `BUSINESS`
 * (`_get_previous_business_tax_payments`) under `businessTaxCalcKeys`. All three cache with
 * `staleTime: Infinity`, so dropping the wrong one forces a full re-request on the other tab.
 *
 * Safe harbor caches under its own namespace rather than `calculateTaxKeys` — the two are separate
 * endpoints — so the quarterly branch has to name both. It was previously omitted, which left a
 * safe-harbor estimate quoting a pre-payment figure after the member recorded one.
 *
 * Invalidate rather than refetch: the keys carry every calculator param, so a refetch would
 * re-request variants nobody is viewing.
 */
export default function invalidateCalculatorsForPayment(queryClient: QueryClient, paymentType?: PaymentType) {
    if (paymentType === PaymentTypes.BUSINESS) {
        return queryClient.invalidateQueries({ queryKey: businessTaxCalcKeys.all });
    }
    if (paymentType === PaymentTypes.QUARTERLY) {
        return Promise.all([
            queryClient.invalidateQueries({ queryKey: calculateTaxKeys.all }),
            queryClient.invalidateQueries({ queryKey: safeHarborCalcKeys.all }),
        ]);
    }

    /*
     * Anything else invalidates both. Two cases reach here: an unresolved type (deleting a payment
     * that is no longer in the cached list), and the six PaymentTypes values neither calculator
     * filters on — CORPORATE, FRANCHISE, PASSTHROUGH_ENTITY, ANNUAL, EXTENSION, OTHER. Matching
     * those to a calculator would be a guess, and a stale figure is a wrong number on screen
     * whereas an extra invalidation costs one refetch.
     */
    return invalidateTaxCalculators(queryClient);
}
